import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import DOMPurify from "dompurify";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as Screenshot } from "../../../assets/icons/svg/screenshot.svg";
import { ReactComponent as Video } from "../../../assets/icons/svg/video.svg";
import { ReactComponent as Share } from "../../../assets/icons/svg/Share.svg";
import { ReactComponent as ArrowLeft } from "../../../assets/icons/svg/Back-arrow.svg";
import { ReactComponent as Filter } from "../../../assets/icons/svg/Filter.svg";
import { ReactComponent as Search } from "../../../assets/icons/svg/Search.svg";
import { ReactComponent as Play } from "../../../assets/icons/svg/play-icon.svg";
import { ReactComponent as Pause } from "../../../assets/icons/svg/pause-icon.svg";
import { ReactComponent as SkipBackward } from "../../../assets/icons/svg/5-sec-backward.svg";
import { ReactComponent as SkipForward } from "../../../assets/icons/svg/5-sec-forward.svg";
import { ReactComponent as VideoPlay } from "../../../assets/icons/svg/VIDEO-PLAY.svg";
import { ReactComponent as VideoPause } from "../../../assets/icons/svg/VIDEO-PAUSE.svg";
import { ReactComponent as Audio } from "../../../assets/icons/svg/audio.svg";
import { ReactComponent as PlayRecording } from "../../../assets/icons/svg/play-recording.svg";
import jiraIcon from "../../../assets/icons/svg/setting/jira.svg";
import { Camera, X, Volume2, VolumeX, Info, Code2, MessageSquare, Monitor, Globe, Wifi, Package, ExternalLink, Clock, Settings2, Maximize2, Scan, Link2, Navigation } from "lucide-react";
import VoiceNoteRecorder from "../../ui/screenshot-screenrecord/Screenshot/VoiceNoteRecorder";
import AudioPlayer from "../../ui/AudioPlayer";
import { useProfileImage } from "../../../contexts/ProfileImageContext";
import ShareFeedbackPopup from "../../ui/ShareFeedbackPopup";
import Button from "../../ui/buttons";
import { shareFeedbackWithDeveloper } from "../../../api/api-utils";
import { API_URLS, BASE_URL, markCardViewedByDeveloper } from "../../../api/urls";
import { DeveloperAudioPlayer } from "../../ui/DeveloperAudioPlayer";
import AvatarIcon from "../../../assets/images/profile-avatar.svg";
import { EnhancedTokenManager } from "../../../utils/tokenManager";
import DeveloperAvatarsGroup from "../../ui/DeveloperAvatarsGroup";
import JiraIntegrationPopup from "../setting_tabs/jira/JiraIntegrationPopup";
import { DEFAULT_FONT_FAMILY } from "../../../config/fontConfig";
import {
  FeedbackItem,
  SharedFeedbackViewProps,
  DeveloperStatus,
  ToastProps,
} from "../../../types/feedback.types";
import {
  stripHtmlTags,
  createVideoUrl,
  isValidVideoSource,
  AudioConverter,
} from "../../../utils/feedbackUtils";
import { TechnicalContext } from "../../../utils/technicalContext";
const Toast: React.FC<ToastProps> = ({ type, title, message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 right-4 z-[60] max-w-md">
      <div
        className={`${type === "success" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"} border rounded-lg p-4 shadow-lg`}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {type === "success" ? (
              <svg
                className="h-5 w-5 text-green-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-red-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <div className="ml-3 flex-1">
            <p
              className={`text-sm font-medium ${type === "success" ? "text-green-800" : "text-red-800"}`}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {title}
            </p>
            <p
              className={`text-sm mt-1 ${type === "success" ? "text-green-700" : "text-red-700"}`}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {message}
            </p>
          </div>
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={onClose}
              className={`${type === "success" ? "bg-green-50 text-green-400 hover:text-green-500" : "bg-red-50 text-red-400 hover:text-red-500"} rounded-md inline-flex focus:outline-none`}
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

class VoiceUploadManager {
  static async uploadVoiceRecording(
    feedbackId: string,
    audioBlob: Blob,
    filename: string,
  ): Promise<{ success: boolean; error?: string; voice_url?: string }> {
    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const numericFeedbackId = String(feedbackId)
        .replace(/^feedback_/, "")
        .trim();
      if (!/^\d+$/.test(numericFeedbackId)) {
        throw new Error(`Invalid feedback ID format: ${feedbackId}`);
      }

      console.log("🎵 Starting voice upload process:", {
        originalFeedbackId: feedbackId,
        numericFeedbackId,
        originalSize: audioBlob.size,
        originalType: audioBlob.type,
        filename,
      });

      let processedAudioFile = await AudioConverter.convertToMP3(audioBlob);
      let processedFileName = filename.replace(/\.(wav|webm|ogg|mp4)$/, ".mp3");

      const formData = new FormData();
      if (
        processedAudioFile instanceof Blob &&
        !(processedAudioFile instanceof File)
      ) {
        const fileObject = new File([processedAudioFile], processedFileName, {
          type: processedAudioFile.type || "audio/mp3",
          lastModified: Date.now(),
        });
        formData.append("file", fileObject);
      } else {
        formData.append("file", processedAudioFile, processedFileName);
      }

      formData.append("feedback_id", numericFeedbackId);
      formData.append("file_type", "audio");

      const response = await fetch(API_URLS.UPLOAD_FILE_VOICE, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorText = await response.text();
          if (errorText) errorMessage = `HTTP ${response.status}: ${errorText}`;
        } catch (e) {
          console.error("🎵 Could not read error response:", e);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("✅ Voice note uploaded successfully:", result);
      return { success: true, voice_url: result.voice_url };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      console.error("❌ Voice upload failed:", error);
      return { success: false, error: errorMessage };
    }
  }
}

const shareCallTracker = new Map<number, boolean>();

const SharedFeedbackView: React.FC<SharedFeedbackViewProps> = ({
  feedbackName,
  totalFeedbacks,
  onBack,
  onShareWithDeveloper,
  feedbackItems = [],
  onUpdateAudio,
  onDeleteFeedback,
  cardId: propCardId,
  workspaceId: propWorkspaceId,
  isSubmittedMode: propIsSubmittedMode = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as {
    fromDeepLink?: boolean;
    returnToDashboard?: boolean;
    cardId?: number;
    developerEmail?: string;
    activeTab?: string;
  } | null;

  const [localFeedbackItems, setLocalFeedbackItems] =
    useState<FeedbackItem[]>(feedbackItems);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(
    feedbackItems.length > 0 ? feedbackItems[0].id : null,
  );
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<
    | "all"
    | "screenshot"
    | "recording"
    | "acknowledged"
    | "unclear"
    | "reshare"
    | "not_read" // ✅ ADDED "reshare"
  >("all");
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoVolume, setVideoVolume] = useState(1);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [forceRecorderMode, setForceRecorderMode] = useState<
    Record<string, boolean>
  >({});
  const [isUploadingAudio, setIsUploadingAudio] = useState<
    Record<string, boolean>
  >({});
  const [uploadError, setUploadError] = useState<Record<string, string>>({});
  const [voiceRecorderKey, setVoiceRecorderKey] = useState(0);
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const [cardId, setCardId] = useState<number | null>(null);
  const [workspaceId, setWorkspaceId] = useState<number | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down" | null>(
    null,
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousFeedback, setPreviousFeedback] = useState<FeedbackItem | null>(
    null,
  );
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isSubmittedMode, setIsSubmittedMode] = useState(false);
  const [feedbackStatuses, setFeedbackStatuses] = useState<
    Record<string, "acknowledged" | "unclear" | "reshare"> // ✅ ADDED "reshare"
  >({});
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showAudioError, setShowAudioError] = useState(false);
  const [audioErrorMessage, setAudioErrorMessage] = useState("");

  //  Overflow detection state
  const [isListOverflowing, setIsListOverflowing] = useState(false);

  // Unclear popup state
  const [showUnclearPopup, setShowUnclearPopup] = useState(false);
  const [unclearReasons, setUnclearReasons] = useState<string[]>([]);
  const [unclearComment, setUnclearComment] = useState("");
  const [isBothSelected, setIsBothSelected] = useState(false);

  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editedFeedbackText, setEditedFeedbackText] = useState("");
  const [textOverflowMap, setTextOverflowMap] = useState<
    Record<string, boolean>
  >({});

  const [actionTakenByAnyDeveloper, setActionTakenByAnyDeveloper] = useState<
    Record<string, boolean>
  >({});

  const [hoveredDeveloperFeedback, setHoveredDeveloperFeedback] = useState<
    string | null
  >(null);
  const [developerNames, setDeveloperNames] = useState<Record<string, string>>(
    {},
  );

  const [isJiraPopupOpen, setIsJiraPopupOpen] = useState(false);
  const [activeRightTab, setActiveRightTab] = useState<'issue' | 'context' | 'comment'>('issue');
  const [environmentCache, setEnvironmentCache] = useState<Record<string, TechnicalContext>>({});
  const [environmentLoading, setEnvironmentLoading] = useState(false);
  const fetchedEnvironmentIds = useRef<Set<string>>(new Set());

  //  toast state
  const [showJiraToast, setShowJiraToast] = useState(false);
  const [jiraToastType, setJiraToastType] = useState<"success" | "error">(
    "success",
  );
  const [jiraToastTitle, setJiraToastTitle] = useState("");
  const [jiraToastMessage, setJiraToastMessage] = useState("");

  const [jiraButtonStates, setJiraButtonStates] = useState<
    Record<
      string,
      {
        isPushed: boolean;
        jiraIssueKey: string | null;
        isLoading: boolean;
      }
    >
  >({});

  const textContentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const searchInputRef = useRef<HTMLInputElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const volumeSliderRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const feedbackListRef = useRef<HTMLDivElement>(null);

  const filteredFeedbackItems = useMemo(() => {
    return localFeedbackItems.filter((item) => {
      // Type filter
      let matchesTypeFilter = true;
      if (selectedFilter === "screenshot" || selectedFilter === "recording") {
        matchesTypeFilter = item.type === selectedFilter;
      }

      // Status filter (only applies when we have developer status tracking)
      let matchesStatusFilter = true;
      if (selectedFilter === "acknowledged") {
        matchesStatusFilter = item.uniqueFeedbackId
          ? feedbackStatuses[item.uniqueFeedbackId] === "acknowledged"
          : false;
      } else if (selectedFilter === "unclear") {
        matchesStatusFilter = item.uniqueFeedbackId
          ? feedbackStatuses[item.uniqueFeedbackId] === "unclear"
          : false;
      } else if (selectedFilter === "reshare") {
        // ✅ NEW FILTER LOGIC
        matchesStatusFilter = item.uniqueFeedbackId
          ? feedbackStatuses[item.uniqueFeedbackId] === "reshare"
          : false;
      } else if (selectedFilter === "not_read") {
        matchesStatusFilter = item.uniqueFeedbackId
          ? !feedbackStatuses[item.uniqueFeedbackId]
          : true;
      }

      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        (item.feedback || item.question || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        item.type.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesTypeFilter && matchesStatusFilter && matchesSearch;
    });
  }, [localFeedbackItems, selectedFilter, searchQuery, feedbackStatuses]);

  const selectedFeedback = useMemo(() => {
    return filteredFeedbackItems.find((item) => item.id === selectedFeedbackId);
  }, [filteredFeedbackItems, selectedFeedbackId]);

  const progressPercentage = useMemo(() => {
    if (!videoDuration || videoDuration <= 0 || isNaN(videoDuration)) return 0;
    if (isNaN(videoCurrentTime) || videoCurrentTime < 0) return 0;
    return Math.max(0, Math.min(100, (videoCurrentTime / videoDuration) * 100));
  }, [videoCurrentTime, videoDuration]);

  const isMac = useMemo(() => {
    return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  }, []);

  const { profileImage } = useProfileImage();

  useEffect(() => {
    const checkAuthentication = async () => {
      const accessToken = EnhancedTokenManager.getAccessToken();

      if (!accessToken) {
        console.log("🔐 No access token found, redirecting to login");
        navigate("/", {
          state: {
            returnUrl: location.pathname + location.search,
            message: "Please sign in to view this feedback",
          },
        });
        return;
      }

      try {
        const response = await fetch(API_URLS.GET_USER_PROFILE, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          console.log("🔐 Token expired, redirecting to login");
          navigate("/", {
            state: {
              returnUrl: location.pathname + location.search,
              message: "Your session expired. Please sign in again.",
            },
          });
          return;
        }

        console.log("✅ Authentication verified");
      } catch (error) {
        console.error("❌ Auth check failed:", error);
        navigate("/", {
          state: {
            returnUrl: location.pathname + location.search,
            message: "Authentication error. Please sign in again.",
          },
        });
      }
    };

    checkAuthentication();
  }, [navigate, location]);

  useEffect(() => {
    let animationFrameId: number;
    const rafLoop = () => {
      const video = document.querySelector("video");
      if (video && !video.paused && !isNaN(video.currentTime)) {
        setVideoCurrentTime(video.currentTime);
      }
      animationFrameId = requestAnimationFrame(rafLoop);
    };
    animationFrameId = requestAnimationFrame(rafLoop);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  useEffect(() => {
    setLocalFeedbackItems(feedbackItems);
  }, [feedbackItems]);

  useEffect(() => {
    if (propCardId) setCardId(propCardId);
    if (propWorkspaceId) setWorkspaceId(propWorkspaceId);
  }, [propCardId, propWorkspaceId]);

  useEffect(() => {
    if (propIsSubmittedMode !== undefined) {
      setIsSubmittedMode(propIsSubmittedMode);
      setFeedbackSubmitted(propIsSubmittedMode);
    }
  }, [propIsSubmittedMode]);

  useEffect(() => {
    if (selectedFeedback?.type !== "recording") {
      setVideoUrl(null);
      return;
    }
    const newVideoUrl = createVideoUrl(selectedFeedback);
    if (newVideoUrl && isValidVideoSource(newVideoUrl)) {
      setVideoUrl(newVideoUrl);
    } else {
      setVideoUrl(null);
    }
  }, [selectedFeedback?.id]);

  useEffect(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (!video || !videoUrl) return;

    video.src = videoUrl;
    setIsVideoPlaying(false);
    setVideoCurrentTime(0);
    setVideoDuration(0);
    setVideoError(null);
    video.volume = videoVolume;
    video.muted = isVideoMuted;

    const handleLoadedMetadata = () => {
      const duration = video.duration;
      if (!isNaN(duration) && duration > 0) {
        setVideoDuration(duration);
        video.volume = videoVolume;
        video.muted = isVideoMuted;
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    return () =>
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
  }, [videoUrl, videoVolume, isVideoMuted]);

  useEffect(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (video) {
      video.volume = videoVolume;
      video.muted = isVideoMuted;
    }
  }, [videoVolume, isVideoMuted, selectedFeedbackId]);

  useEffect(() => {
    if (selectedFeedback?.type !== "recording") {
      setIsVideoPlaying(false);
      setVideoCurrentTime(0);
      setVideoDuration(0);
      setVideoError(null);
      setShowVolumeSlider(false);
      const video = document.querySelector("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
        video.src = "";
      }
    }
  }, [selectedFeedback?.type]);

  useEffect(() => {
    if (selectedFeedback?.type === "screenshot") {
      setVoiceRecorderKey((prev) => prev + 1);
    }
  }, [selectedFeedbackId, selectedFeedback?.uniqueFeedbackId]);

  useEffect(() => {
    setActiveRightTab('issue');
  }, [selectedFeedbackId]);

  useEffect(() => {
    if (!selectedFeedback?.apiFeedbackId) return;
    const feedbackId = String(selectedFeedback.apiFeedbackId);
    if (fetchedEnvironmentIds.current.has(feedbackId)) return;
    fetchedEnvironmentIds.current.add(feedbackId);

    const fetchEnvironment = async () => {
      setEnvironmentLoading(true);
      try {
        const accessToken = EnhancedTokenManager.getAccessToken();
        if (!accessToken) return;
        const response = await fetch(API_URLS.FEEDBACK_ENVIRONMENT(feedbackId), {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        const ctx = data?.environment_data?.technical_context;
        if (ctx) {
          setEnvironmentCache(prev => ({ ...prev, [feedbackId]: ctx }));
        }
      } catch {
        // context tab will show "no data" message
      } finally {
        setEnvironmentLoading(false);
      }
    };

    fetchEnvironment();
  }, [selectedFeedbackId, selectedFeedback?.apiFeedbackId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🆕 NEW: Detect overflow in feedback list
  useEffect(() => {
    const checkOverflow = () => {
      if (feedbackListRef.current) {
        const isOverflowing =
          feedbackListRef.current.scrollHeight >
          feedbackListRef.current.clientHeight;
        setIsListOverflowing(isOverflowing);
      }
    };

    // Check on mount and when items change
    checkOverflow();

    // Check on window resize
    window.addEventListener("resize", checkOverflow);

    return () => window.removeEventListener("resize", checkOverflow);
  }, [filteredFeedbackItems.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isSearchOpen &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
        setSearchQuery("");
      }
      if (
        isFilterOpen &&
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
      if (
        showVolumeSlider &&
        volumeRef.current &&
        !volumeRef.current.contains(event.target as Node)
      ) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSearchOpen, isFilterOpen, showVolumeSlider]);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const uploadVoiceRecording = useCallback(
    async (feedbackId: string, audioBlob: Blob, filename: string) => {
      if (!selectedFeedback?.uniqueFeedbackId) return false;
      const uniqueId = selectedFeedback.uniqueFeedbackId;

      setIsUploadingAudio((prev) => ({ ...prev, [uniqueId]: true }));
      setUploadError((prev) => {
        const updated = { ...prev };
        delete updated[uniqueId];
        return updated;
      });

      try {
        const result = await VoiceUploadManager.uploadVoiceRecording(
          uniqueId,
          audioBlob,
          filename,
        );
        if (result.success) {
          setForceRecorderMode((prev) => ({ ...prev, [uniqueId]: false }));

          const updatedItems = localFeedbackItems.map((item) => {
            if (item.id === feedbackId) {
              return { ...item, hasAudio: true, audioUrl: result.voice_url };
            }
            return item;
          });
          setLocalFeedbackItems(updatedItems);
          onUpdateAudio?.(feedbackId, true, result.voice_url);
          setTimeout(() => setVoiceRecorderKey((prev) => prev + 1), 100);
          return true;
        } else {
          throw new Error(result.error || "Upload failed");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setUploadError((prev) => ({ ...prev, [uniqueId]: errorMessage }));
        return false;
      } finally {
        setIsUploadingAudio((prev) => ({ ...prev, [uniqueId]: false }));
      }
    },
    [selectedFeedback, localFeedbackItems, onUpdateAudio],
  );

  const handleVideoPlay = useCallback(() => {
    setIsVideoPlaying(true);
    setVideoError(null);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsVideoPlaying(false);
  }, []);

  const handleVideoError = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      const error = e.currentTarget.error;
      setVideoError(error ? `Video error: ${error.message}` : "Video error");
      setIsVideoPlaying(false);
    },
    [],
  );

  const toggleVideoPlayPause = useCallback(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (!video || !videoUrl) {
      setVideoError("Video not available");
      return;
    }
    try {
      if (isVideoPlaying) {
        video.pause();
      } else {
        const playPromise = video.play();
        if (playPromise) {
          playPromise.catch((error) =>
            setVideoError("Playback failed: " + error.message),
          );
        }
      }
    } catch (error) {
      setVideoError(error instanceof Error ? error.message : "unknown");
    }
  }, [isVideoPlaying, videoUrl]);

  const handleProgressBarClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const video = document.querySelector("video") as HTMLVideoElement;
      if (!video || videoDuration <= 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const percentage = (e.clientX - rect.left) / rect.width;
      const newTime = Math.max(
        0,
        Math.min(videoDuration, percentage * videoDuration),
      );
      video.currentTime = newTime;
      setVideoCurrentTime(newTime);
    },
    [videoDuration],
  );

  const skipBackward = useCallback(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (video) {
      video.currentTime = Math.max(0, video.currentTime - 5);
      setVideoCurrentTime(video.currentTime);
    }
  }, []);

  const skipForward = useCallback(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (video && videoDuration) {
      video.currentTime = Math.min(videoDuration, video.currentTime + 5);
      setVideoCurrentTime(video.currentTime);
    }
  }, [videoDuration]);

  const toggleVideoMute = useCallback(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (video) {
      const newMuted = !isVideoMuted;
      video.muted = newMuted;
      setIsVideoMuted(newMuted);
    }
  }, [isVideoMuted]);

  const toggleVolumeSlider = useCallback(() => {
    setShowVolumeSlider(!showVolumeSlider);
  }, [showVolumeSlider]);

  const handleContinueEditing = useCallback(() => {
    onBack?.();
  }, [onBack]);

  const handleBackToDashboard = useCallback(() => {
    console.log("⬅️ Back button clicked - returning to dashboard");

    if (onBack) {
      onBack();
    } else {
      navigate("/dashboard", {
        state: {
          activeTab: "shared",
          preserveData: true,
        },
        replace: true,
      });
    }
  }, [navigate, onBack]);

  const handleSubmitFeedbackClick = useCallback(() => {
    setShowSubmitConfirm(true);
  }, []);

  const handleSubmitConfirm = useCallback(() => {
    setShowSubmitConfirm(false);
    setFeedbackSubmitted(true);
    setIsSubmittedMode(true);
  }, []);

  const handleSubmitCancel = useCallback(() => {
    setShowSubmitConfirm(false);
  }, []);

  const handleShareWithDeveloper = useCallback(() => {
    if (isSharing || (cardId && shareCallTracker.get(cardId))) return;
    if (!cardId) {
      alert("Unable to share: Card ID not found");
      return;
    }
    setIsSharePopupOpen(true);
  }, [cardId, isSharing]);

  useEffect(() => {
    return () => {
      if (cardId && shareCallTracker.has(cardId)) {
        shareCallTracker.delete(cardId);
      }
    };
  }, [cardId]);

  const hasMarkedViewedRef = useRef(false);
  useEffect(() => {
    if (!cardId || hasMarkedViewedRef.current) return;
    const accessToken = EnhancedTokenManager.getAccessToken();
    if (!accessToken) return;

    hasMarkedViewedRef.current = true;

    // Update localStorage cache
    const storedViewedCards = localStorage.getItem("viewed_developer_cards");
    const viewedSet = storedViewedCards
      ? new Set(JSON.parse(storedViewedCards))
      : new Set();
    viewedSet.add(cardId);
    localStorage.setItem(
      "viewed_developer_cards",
      JSON.stringify(Array.from(viewedSet)),
    );

    // Notify backend — fire and forget, non-critical
    markCardViewedByDeveloper(cardId, accessToken).catch(() => {});
  }, [cardId]);

  const handleShareEmails = useCallback(
    async (emails: string[]) => {
      if (!cardId) {
        alert("Unable to share: Card ID not found");
        return;
      }
      if (shareCallTracker.get(cardId) || isSharing) return;

      shareCallTracker.set(cardId, true);
      setIsSharing(true);
      setIsSharePopupOpen(false);

      const uniqueEmails = Array.from(
        new Set(emails.map((email) => email.trim().toLowerCase())),
      ).filter((email) => email.length > 0);

      if (uniqueEmails.length === 0) {
        shareCallTracker.set(cardId, false);
        setIsSharing(false);
        alert("No valid email addresses provided");
        return;
      }

      try {
        const result = await shareFeedbackWithDeveloper(
          cardId,
          uniqueEmails,
          workspaceId || undefined,
        );
        if (result.success && result.data) {
          const expectedUrls = uniqueEmails.length;
          const actualUrls = result.data.developer_feedback_urls.length;
          const successMessage =
            actualUrls === expectedUrls
              ? `Complete feedback card shared with ${uniqueEmails.length} developer(s).`
              : `Card shared but received ${actualUrls} URLs for ${expectedUrls} developers.`;
          alert(successMessage);
          onShareWithDeveloper?.({
            success: true,
            cardId,
            feedbackName: feedbackName || "Untitled Feedback",
            emails: uniqueEmails,
            feedbackCount: totalFeedbacks || 0,
          });
        } else {
          throw new Error(result.error || "Share failed");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        alert(`Failed to share feedback card: ${errorMessage}`);
        setIsSharePopupOpen(true);
      } finally {
        setTimeout(() => {
          shareCallTracker.set(cardId, false);
          setIsSharing(false);
        }, 2000);
      }
    },
    [
      cardId,
      workspaceId,
      totalFeedbacks,
      feedbackName,
      onShareWithDeveloper,
      isSharing,
    ],
  );

  const handleFeedbackSelect = useCallback(
    (feedbackId: string) => {
      if (feedbackId === selectedFeedbackId || isAnimating) return;
      const currentIndex = filteredFeedbackItems.findIndex(
        (item) => item.id === selectedFeedbackId,
      );
      const nextIndex = filteredFeedbackItems.findIndex(
        (item) => item.id === feedbackId,
      );

      if (currentIndex !== -1 && nextIndex !== -1) {
        const direction = nextIndex > currentIndex ? "down" : "up";
        setPreviousFeedback(selectedFeedback || null);
        setScrollDirection(direction);
        setIsAnimating(true);
        setSelectedFeedbackId(feedbackId);

        // 🆕 Auto-scroll the left list to show the selected item
        if (feedbackListRef.current) {
          const itemHeight = 112; // h-28 = 7rem = 112px
          const scrollPosition = nextIndex * itemHeight;
          const containerHeight = feedbackListRef.current.clientHeight;
          const currentScroll = feedbackListRef.current.scrollTop;

          // Only scroll if the item is not fully visible
          if (
            scrollPosition < currentScroll ||
            scrollPosition > currentScroll + containerHeight - itemHeight
          ) {
            feedbackListRef.current.scrollTo({
              top: Math.max(
                0,
                scrollPosition - containerHeight / 2 + itemHeight / 2,
              ),
              behavior: "smooth",
            });
          }
        }

        setTimeout(() => {
          setIsAnimating(false);
          setScrollDirection(null);
          setPreviousFeedback(null);
        }, 300);
      } else {
        setSelectedFeedbackId(feedbackId);
      }
    },
    [selectedFeedbackId, selectedFeedback, filteredFeedbackItems, isAnimating],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isSearchOpen) return;
      const currentIndex = filteredFeedbackItems.findIndex(
        (item) => item.id === selectedFeedbackId,
      );
      if (e.key === "ArrowUp" && currentIndex > 0) {
        e.preventDefault();
        handleFeedbackSelect(filteredFeedbackItems[currentIndex - 1].id);
      } else if (
        e.key === "ArrowDown" &&
        currentIndex < filteredFeedbackItems.length - 1
      ) {
        e.preventDefault();
        handleFeedbackSelect(filteredFeedbackItems[currentIndex + 1].id);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedFeedbackId,
    filteredFeedbackItems,
    isSearchOpen,
    handleFeedbackSelect,
  ]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!contentContainerRef.current || isAnimating) return;
      const container = contentContainerRef.current;
      const target = e.target as HTMLElement;
      if (!container.contains(target)) return;

      const isScrollable = (element: HTMLElement): boolean => {
        const overflowY = window.getComputedStyle(element).overflowY;
        return overflowY === "scroll" || overflowY === "auto";
      };

      let currentElement = target;
      while (currentElement && currentElement !== container) {
        if (
          isScrollable(currentElement) &&
          currentElement.scrollHeight > currentElement.clientHeight
        ) {
          return;
        }
        currentElement = currentElement.parentElement as HTMLElement;
      }

      const currentIndex = filteredFeedbackItems.findIndex(
        (item) => item.id === selectedFeedbackId,
      );

      if (e.deltaY > 0 && currentIndex < filteredFeedbackItems.length - 1) {
        e.preventDefault();
        const nextItem = filteredFeedbackItems[currentIndex + 1];
        handleFeedbackSelect(nextItem.id);

        // 🆕 Scroll the left list to show the selected item
        if (feedbackListRef.current) {
          const itemHeight = 112; // h-28 = 7rem = 112px (adjust for 3xl:h-32 if needed)
          const scrollPosition = (currentIndex + 1) * itemHeight;
          feedbackListRef.current.scrollTo({
            top: scrollPosition,
            behavior: "smooth",
          });
        }
      } else if (e.deltaY < 0 && currentIndex > 0) {
        e.preventDefault();
        const prevItem = filteredFeedbackItems[currentIndex - 1];
        handleFeedbackSelect(prevItem.id);

        // 🆕 Scroll the left list to show the selected item
        if (feedbackListRef.current) {
          const itemHeight = 112;
          const scrollPosition = (currentIndex - 1) * itemHeight;
          feedbackListRef.current.scrollTo({
            top: scrollPosition,
            behavior: "smooth",
          });
        }
      }
    };

    const container = contentContainerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
      return () => container.removeEventListener("wheel", handleWheel);
    }
  }, [
    selectedFeedbackId,
    filteredFeedbackItems,
    isAnimating,
    handleFeedbackSelect,
  ]);

  const handleSearchClick = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const handleFilterClick = useCallback(() => {
    setIsFilterOpen(!isFilterOpen);
  }, [isFilterOpen]);

  const handleFilterSelect = useCallback(
    (
      filter:
        | "all"
        | "screenshot"
        | "recording"
        | "acknowledged"
        | "unclear"
        | "reshare" // ✅ ADDED
        | "not_read",
    ) => {
      setSelectedFilter(filter);
      setIsFilterOpen(false);
    },
    [],
  );
  const handleRecordingComplete = useCallback(
    async (
      hasRecording: boolean,
      audioUrl?: string,
      downloadUrl?: string,
      filename?: string,
    ) => {
      if (!selectedFeedback?.uniqueFeedbackId) return;
      const uniqueId = selectedFeedback.uniqueFeedbackId;

      if (hasRecording && audioUrl && filename) {
        const response = await fetch(audioUrl);
        const audioBlob = await response.blob();

        const updatedItems = localFeedbackItems.map((item) => {
          if (item.id === selectedFeedback.id) {
            return { ...item, hasAudio: true, audioUrl };
          }
          return item;
        });
        setLocalFeedbackItems(updatedItems);

        const uploadSuccess = await uploadVoiceRecording(
          selectedFeedback.id,
          audioBlob,
          filename,
        );

        if (!uploadSuccess) {
          setForceRecorderMode((prev) => ({ ...prev, [uniqueId]: true }));
        }
      } else {
        setForceRecorderMode((prev) => ({ ...prev, [uniqueId]: true }));
        const updatedItems = localFeedbackItems.map((item) => {
          if (item.id === selectedFeedback.id) {
            return { ...item, hasAudio: false, audioUrl: undefined };
          }
          return item;
        });
        setLocalFeedbackItems(updatedItems);
        onUpdateAudio?.(selectedFeedback.id, false);
      }
    },
    [selectedFeedback, localFeedbackItems, onUpdateAudio, uploadVoiceRecording],
  );

  const handleAudioDelete = useCallback(
    (feedbackId: string, uniqueFeedbackId: string) => {
      setForceRecorderMode((prev) => ({ ...prev, [uniqueFeedbackId]: true }));
      setUploadError((prev) => {
        const updated = { ...prev };
        delete updated[uniqueFeedbackId];
        return updated;
      });

      const updatedItems = localFeedbackItems.map((item) => {
        if (item.id === feedbackId) {
          return { ...item, hasAudio: false, audioUrl: undefined };
        }
        return item;
      });
      setLocalFeedbackItems(updatedItems);
      onUpdateAudio?.(feedbackId, false);
      setVoiceRecorderKey((prev) => prev + 1);
    },
    [localFeedbackItems, onUpdateAudio],
  );

  const handleAudioPlayerError = useCallback(
    (error: string) => {
      if (selectedFeedback?.uniqueFeedbackId) {
        const uniqueId = selectedFeedback.uniqueFeedbackId;
        setForceRecorderMode((prev) => ({ ...prev, [uniqueId]: true }));
        setUploadError((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });
        setVoiceRecorderKey((prev) => prev + 1);
      }
    },
    [selectedFeedback?.uniqueFeedbackId],
  );

  const handleNoAudioClick = useCallback(() => {
    setAudioErrorMessage("No audio available for this feedback");
    setShowAudioError(true);
    setTimeout(() => setShowAudioError(false), 3000);
  }, []);

  const handleVolumeSliderMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const updateVolume = (clientY: number) => {
        if (!volumeSliderRef.current) return;
        const rect = volumeSliderRef.current.getBoundingClientRect();
        const y = clientY - rect.top;
        const percentage = Math.max(0, Math.min(1, 1 - y / rect.height));
        setVideoVolume(percentage);

        const video = document.querySelector("video") as HTMLVideoElement;
        if (video) {
          video.volume = percentage;
          if (percentage === 0) {
            video.muted = true;
            setIsVideoMuted(true);
          } else if (isVideoMuted) {
            video.muted = false;
            setIsVideoMuted(false);
          }
        }
      };

      updateVolume(e.clientY);
      const handleMouseMove = (moveEvent: MouseEvent) =>
        updateVolume(moveEvent.clientY);
      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };

      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [isVideoMuted],
  );

  // ✅ useEffect #1 - Initialize statuses from pre-loaded feedback items (INSTANT)
  useEffect(() => {
    if (feedbackItems.length > 0) {
      // ✅ CRITICAL: Don't trust pre-loaded statuses
      // They might contain other developers' responses
      console.log(
        "⚠️ Skipping pre-loaded statuses - will fetch from API with user filtering",
      );
      setFeedbackStatuses({});
    }
  }, [feedbackItems]);

  // ✅ useEffect #2 - Background fetch (ONLY if no pre-loaded data)
  useEffect(() => {
    const fetchAllFeedbackStatusesFromCard = async () => {
      if (feedbackItems.length === 0) {
        return;
      }

      if (!cardId) {
        console.warn("⚠️ No cardId available for batch status fetch");
        return;
      }

      console.log("🔄 Fetching statuses for card:", cardId);

      try {
        const accessToken = EnhancedTokenManager.getAccessToken();
        if (!accessToken) {
          return;
        }

        const currentUserEmail = EnhancedTokenManager.getCurrentUserEmail();
        if (!currentUserEmail) {
          console.error("❌ Cannot determine current user email");
          return;
        }

        console.log("👤 Current logged-in user:", currentUserEmail);

        const response = await fetch(
          API_URLS.GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE(cardId),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          console.error("❌ Failed to fetch card statuses:", response.status);
          return;
        }

        const data = await response.json();
        console.log("📊 Full API response:", data);

        const statusMap: Record<
          string,
          "acknowledged" | "unclear" | "reshare"
        > = {};
        const actionTakenMap: Record<string, boolean> = {}; // ✅ NEW: Track if ANY developer has responded
        const developerInfoMap: Record<
          string,
          {
            profileImage: string | null;
            email: string;
            status: "acknowledged" | "unclear" | "reshare";
          }
        > = {};
        const developerNamesMap: Record<string, string> = {};

        if (data.feedbacks && Array.isArray(data.feedbacks)) {
          data.feedbacks.forEach((feedback: any) => {
            const feedbackId = feedback.feedback_id?.toString();

            if (!feedbackId) return;

            console.log(`\n🔍 Processing Feedback ${feedbackId}:`);
            console.log(`   Developers in response:`, feedback.developers);
            console.log(`   Card-level status:`, feedback.status);

            // ✅ FIXED: Check if ANY developer has taken action (has developers array with ACTUAL actions)
            // Previously: Just checked if developers array exists
            // Now: Check if there are actual acknowledged/unclear responses
            const hasDeveloperActions =
              feedback.developers &&
              Array.isArray(feedback.developers) &&
              feedback.developers.length > 0 &&
              feedback.developers.some(
                (dev: any) =>
                  dev.status &&
                  (dev.status.toLowerCase() === "acknowledged" ||
                    dev.status.toLowerCase() === "unclear"),
              );

            if (hasDeveloperActions) {
              actionTakenMap[feedbackId] = true;
              console.log(
                `   ✅ Developer actions exist for feedback ${feedbackId}`,
              );
            } else {
              actionTakenMap[feedbackId] = false;
              console.log(
                `   ℹ️ No developer actions for feedback ${feedbackId}`,
              );
            }

            // Check if CURRENT USER has responded
            if (feedback.developers && Array.isArray(feedback.developers)) {
              const currentUserStatus = feedback.developers.find(
                (dev: any) => dev.developer_email === currentUserEmail,
              );

              if (currentUserStatus && currentUserStatus.status) {
                const status = currentUserStatus.status.toLowerCase();

                if (
                  status === "acknowledged" ||
                  status === "unclear" ||
                  status === "reshare"
                ) {
                  statusMap[feedbackId] = status as
                    | "acknowledged"
                    | "unclear"
                    | "reshare";
                  console.log(
                    `   ✅ Current user (${currentUserEmail}) has responded: ${status}`,
                  );
                }
              } else {
                console.log(
                  `   ℹ️ Current user (${currentUserEmail}) has NOT responded yet`,
                );
              }

              // Find first developer with status for profile image display
              const firstDeveloperWithStatus = feedback.developers.find(
                (dev: any) =>
                  dev.status &&
                  (dev.status.toLowerCase() === "acknowledged" ||
                    dev.status.toLowerCase() === "unclear" ||
                    dev.status.toLowerCase() === "reshare"),
              );

              if (firstDeveloperWithStatus) {
                const statusLower =
                  firstDeveloperWithStatus.status.toLowerCase();

                // Store developer info for display (only if not reshare)
                if (statusLower !== "reshare") {
                  developerInfoMap[feedbackId] = {
                    profileImage: firstDeveloperWithStatus.profile_image,
                    email: firstDeveloperWithStatus.developer_email,
                    status: statusLower as
                      | "acknowledged"
                      | "unclear"
                      | "reshare",
                  };

                  // Store developer name
                  if (
                    firstDeveloperWithStatus.developer_name ||
                    firstDeveloperWithStatus.developer_email
                  ) {
                    const displayName =
                      firstDeveloperWithStatus.developer_name ||
                      firstDeveloperWithStatus.developer_email.split("@")[0];
                    developerNamesMap[feedbackId] = displayName;

                    console.log(
                      `✅ Extracted developer name for ${feedbackId}: ${displayName}`,
                    );
                  }
                }
              }
            }

            // ✅ Check card-level reshare status (applies to ALL users if no developer actions)
            if (
              feedback.status &&
              feedback.status.toLowerCase() === "reshare"
            ) {
              // Only set reshare status if current user hasn't already responded
              if (!statusMap[feedbackId]) {
                statusMap[feedbackId] = "reshare";
                console.log(
                  `✅ Using card-level reshare for feedback ${feedbackId}`,
                );
              }
            }

            console.log(`ℹ️ Final processing for ${feedbackId}:`, {
              status: statusMap[feedbackId] || "no status",
              actionTaken: actionTakenMap[feedbackId],
            });
          });
        }

        console.log("\n📋 Final status map for current user:", statusMap);
        console.log("🔒 Action taken map:", actionTakenMap);
        console.log("👤 Developer info map:", developerInfoMap);
        console.log("📝 Developer names map:", developerNamesMap);

        // ✅ NEW: Debug log to verify the maps are correct
        console.log("\n🔍 DETAILED BUTTON LOGIC DEBUG:");
        Object.keys(statusMap).forEach((feedbackId) => {
          const status = statusMap[feedbackId];
          const actionTaken = actionTakenMap[feedbackId];
          const hasUserResponded = status && status !== "reshare";

          // ✅ UPDATED LOGIC: Reshared + No Actions = Show buttons
          const shouldHide = actionTaken && !hasUserResponded;

          console.log(`   Feedback ${feedbackId}:`, {
            status,
            actionTaken,
            hasUserResponded,
            shouldHideButtons: shouldHide,
            reason: shouldHide
              ? "Other developer has already responded"
              : status === "reshare" && !actionTaken
                ? "Reshared - no actions yet (buttons visible)"
                : "Can respond",
          });
        });

        setFeedbackStatuses(statusMap);
        setActionTakenByAnyDeveloper(actionTakenMap); // ✅ IMPORTANT: Update action tracking
        setDeveloperNames(developerNamesMap);

        setLocalFeedbackItems((prevItems) =>
          prevItems.map((item) => {
            const feedbackId = item.uniqueFeedbackId;
            if (feedbackId && developerInfoMap[feedbackId]) {
              const devInfo = developerInfoMap[feedbackId];

              return {
                ...item,
                developerProfileImage:
                  devInfo.status !== "reshare"
                    ? devInfo.profileImage || undefined
                    : undefined,
                developerEmail: devInfo.email || undefined,
                developerActionStatus: devInfo.status,
              };
            }

            return item;
          }),
        );
      } catch (error) {
        console.error("❌ Error fetching card statuses:", error);
      }
    };

    if (feedbackItems.length > 0 && cardId) {
      fetchAllFeedbackStatusesFromCard();
    }
  }, [feedbackItems, cardId]);

  useEffect(() => {
    const currentUserEmail = EnhancedTokenManager.getCurrentUserEmail();
    console.log("\n========================================");
    console.log("🔐 DEBUG: Current Session Info");
    console.log("========================================");
    console.log("👤 Logged in as:", currentUserEmail);
    console.log("📋 Current statuses:", feedbackStatuses);
    console.log("🎯 Selected feedback:", selectedFeedbackId);

    if (selectedFeedbackId) {
      const status = feedbackStatuses[selectedFeedbackId];
      console.log(
        `📊 Selected feedback status for ${currentUserEmail}:`,
        status || "NO STATUS (hasn't responded)",
      );
    }
    console.log("========================================\n");
  }, [feedbackStatuses, selectedFeedbackId]);

  useEffect(() => {
    if (feedbackItems.length > 0) {
      console.log("📊 Syncing local feedback items with props");
      setLocalFeedbackItems(feedbackItems);
    }
  }, [feedbackItems]);

  useEffect(() => {
    // When filter changes, select the first item in the filtered list
    if (filteredFeedbackItems.length > 0) {
      const firstItemId = filteredFeedbackItems[0].id;

      // Only update if it's different from current selection
      if (selectedFeedbackId !== firstItemId) {
        setSelectedFeedbackId(firstItemId);

        // Auto-scroll the left list to show the selected item
        if (feedbackListRef.current) {
          const itemHeight = 112; // h-28 = 7rem = 112px
          feedbackListRef.current.scrollTo({
            top: 0, // Scroll to top when filter changes
            behavior: "smooth",
          });
        }
      }
    } else {
      // If no items in filtered list, clear selection
      setSelectedFeedbackId(null);
    }
  }, [selectedFilter, filteredFeedbackItems.length]);

  useEffect(() => {
    if (!selectedFeedback) return;

    const checkOverflow = () => {
      const element = textContentRefs.current.get(selectedFeedback.id);
      if (element) {
        const lineHeight = parseFloat(
          window.getComputedStyle(element).lineHeight,
        );
        const maxHeight = lineHeight * 1.5; // 1.5 lines
        const isOverflowing = element.scrollHeight > maxHeight + 1;

        setTextOverflowMap((prev) => {
          if (prev[selectedFeedback.id] !== isOverflowing) {
            return {
              ...prev,
              [selectedFeedback.id]: isOverflowing,
            };
          }
          return prev;
        });
      }
    };

    // Check immediately and after a small delay for DOM rendering
    checkOverflow();
    const timeoutId = setTimeout(checkOverflow, 100);

    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
      clearTimeout(timeoutId);
    };
  }, [
    selectedFeedback?.id,
    selectedFeedback?.feedback,
    selectedFeedback?.question,
  ]);

  const handleShowMoreClick = useCallback(() => {
    if (selectedFeedback) {
      setEditedFeedbackText(
        selectedFeedback.feedback || selectedFeedback.question || "",
      );
      setShowEditPopup(true);
    }
  }, [selectedFeedback]);

  const handleCancelEdit = useCallback(() => {
    setShowEditPopup(false);
    setEditedFeedbackText("");
  }, []);

  const checkDeveloperActionStatus = useCallback(async (feedbackId: string) => {
    if (!feedbackId) return;

    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        console.warn("No access token for action status check");
        return;
      }

      const numericFeedbackId = feedbackId.replace(/^feedback_/, "");

      const response = await fetch(
        API_URLS.GET_DEVELOPER_ACTION_STATUS(parseInt(numericFeedbackId)),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        console.error(
          "Failed to fetch developer action status:",
          response.status,
        );
        return;
      }

      const data = await response.json();
      console.log("🔍 Developer action status response:", data);

      // ✅ CRITICAL: Update state based on action_taken flag
      // action_taken: true means AT LEAST ONE developer has responded
      if (data && typeof data.action_taken === "boolean") {
        console.log(
          `📊 Feedback ${feedbackId}: action_taken = ${data.action_taken}`,
        );
        setActionTakenByAnyDeveloper((prev) => ({
          ...prev,
          [feedbackId]: data.action_taken,
        }));
      }
    } catch (error) {
      console.error("Error checking developer action status:", error);
    }
  }, []);

  const checkJiraButtonState = useCallback(async (feedbackId: string) => {
    if (!feedbackId) return;

    const numericFeedbackId = feedbackId.replace(/^feedback_/, "");

    // Set loading state
    setJiraButtonStates((prev) => ({
      ...prev,
      [feedbackId]: {
        isPushed: prev[feedbackId]?.isPushed || false,
        jiraIssueKey: prev[feedbackId]?.jiraIssueKey || null,
        isLoading: true,
      },
    }));

    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        console.warn("No access token for Jira status check");
        setJiraButtonStates((prev) => ({
          ...prev,
          [feedbackId]: {
            isPushed: false,
            jiraIssueKey: null,
            isLoading: false,
          },
        }));
        return;
      }

      const response = await fetch(
        API_URLS.CHECK_JIRA_BUTTON_STATUS(parseInt(numericFeedbackId)),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        console.error("Failed to fetch Jira button status:", response.status);
        setJiraButtonStates((prev) => ({
          ...prev,
          [feedbackId]: {
            isPushed: false,
            jiraIssueKey: null,
            isLoading: false,
          },
        }));
        return;
      }

      const data = await response.json();
      console.log("🎫 Jira button status response:", data);

      setJiraButtonStates((prev) => ({
        ...prev,
        [feedbackId]: {
          isPushed: data.is_pushed_to_jira || false,
          jiraIssueKey: data.jira_issue_key || null,
          isLoading: false,
        },
      }));
    } catch (error) {
      console.error("Error checking Jira button status:", error);
      setJiraButtonStates((prev) => ({
        ...prev,
        [feedbackId]: {
          isPushed: false,
          jiraIssueKey: null,
          isLoading: false,
        },
      }));
    }
  }, []);

  //check action status when selected feedback changes
  useEffect(() => {
    if (selectedFeedback?.uniqueFeedbackId) {
      checkDeveloperActionStatus(selectedFeedback.uniqueFeedbackId);
      // Also check Jira button state when feedback is selected
      checkJiraButtonState(selectedFeedback.uniqueFeedbackId);
    }
  }, [
    selectedFeedback?.uniqueFeedbackId,
    checkDeveloperActionStatus,
    checkJiraButtonState,
  ]);

  useEffect(() => {
    const highlightFeedbackId = localStorage.getItem("highlight_feedback_id");
    const reshareAction = localStorage.getItem("feedback_reshare_action");

    if (highlightFeedbackId && localFeedbackItems.length > 0) {
      console.log("🎯 SharedFeedbackView: Highlighting reshared feedback:", {
        feedbackId: highlightFeedbackId,
        reshareAction,
        totalItems: localFeedbackItems.length,
        isReshare: !!reshareAction,
      });

      // Wait for DOM to render, then scroll to highlighted feedback
      setTimeout(() => {
        // Find the feedback item by uniqueFeedbackId or id
        const targetFeedback = localFeedbackItems.find(
          (item) =>
            item.uniqueFeedbackId === highlightFeedbackId ||
            item.id === highlightFeedbackId ||
            String(item.id).includes(highlightFeedbackId),
        );

        if (targetFeedback) {
          console.log("✅ Found target reshared feedback:", {
            id: targetFeedback.id,
            uniqueFeedbackId: targetFeedback.uniqueFeedbackId,
            type: targetFeedback.type,
            isReshare: !!reshareAction,
          });

          // Select the feedback item (shows it in the right panel)
          setSelectedFeedbackId(targetFeedback.id);

          // Scroll to the feedback item in the left sidebar
          if (feedbackListRef.current) {
            // Calculate the index of this feedback in the filtered list
            const feedbackIndex = filteredFeedbackItems.findIndex(
              (item) => item.id === targetFeedback.id,
            );

            if (feedbackIndex !== -1) {
              const itemHeight = 112; // h-28 = 7rem = 112px (adjust if using 3xl:h-32)
              const scrollPosition = feedbackIndex * itemHeight;
              const containerHeight = feedbackListRef.current.clientHeight;

              // Scroll to center the item in view
              feedbackListRef.current.scrollTo({
                top: Math.max(
                  0,
                  scrollPosition - containerHeight / 2 + itemHeight / 2,
                ),
                behavior: "smooth",
              });

              console.log(
                "✅ Scrolled to feedback item at index:",
                feedbackIndex,
              );
            }
          }

          // Apply highlight animation to the feedback item
          // Wait a bit for scroll to complete before applying animation
          setTimeout(() => {
            // Find all feedback items in the sidebar
            const feedbackItems =
              document.querySelectorAll("[data-feedback-id]");

            feedbackItems.forEach((element) => {
              if (
                element.getAttribute("data-feedback-id") === targetFeedback.id
              ) {
                console.log("✅ Applying highlight animation to feedback item");
                element.classList.add("feedback-highlight-pulse");

                // Remove highlight after animation completes (3 seconds)
                setTimeout(() => {
                  element.classList.remove("feedback-highlight-pulse");
                  console.log("✅ Highlight animation completed");
                }, 3000);
              }
            });
          }, 600); // Wait for smooth scroll to complete
        } else {
          console.warn(
            "⚠️ Reshared feedback not found in list:",
            highlightFeedbackId,
          );
        }

        // Clear localStorage after highlighting (whether found or not)
        localStorage.removeItem("highlight_feedback_id");
        localStorage.removeItem("feedback_reshare_action");
        console.log("✅ Cleared reshare highlight flags from localStorage");
      }, 500); // 500ms delay to ensure DOM is fully rendered
    }
  }, [localFeedbackItems, filteredFeedbackItems, setSelectedFeedbackId]);

  const handleStatusUpdate = useCallback(
    async (action: "acknowledged" | "unclear") => {
      if (!selectedFeedback?.uniqueFeedbackId || isUpdatingStatus) return;

      if (action === "unclear") {
        const uniqueId = selectedFeedback.uniqueFeedbackId;
        const currentStatus = feedbackStatuses[uniqueId];

        if (currentStatus === "acknowledged") {
          return;
        }

        setShowUnclearPopup(true);
        setUnclearReasons([]);
        setUnclearComment("");
        return;
      }

      const uniqueId = selectedFeedback.uniqueFeedbackId;
      const currentStatus = feedbackStatuses[uniqueId];

      const updateTimestamp = Date.now();
      sessionStorage.setItem(
        `status_update_${uniqueId}`,
        updateTimestamp.toString(),
      );

      // ✅ NEW: Get current user's profile and email
      const currentUserEmail = EnhancedTokenManager.getCurrentUserEmail();

      // ✅ NEW: Use a special flag to indicate "use current user's profile"
      // We'll use '__CURRENT_USER__' as a marker that will be handled in rendering
      const developerImageMarker = "__CURRENT_USER__";

      setFeedbackStatuses((prev) => ({
        ...prev,
        [uniqueId]: action,
      }));

      // ✅ FIXED: Use special marker to indicate current user's profile should be used
      setLocalFeedbackItems((prev) =>
        prev.map((item) =>
          item.id === selectedFeedback.id
            ? {
                ...item,
                developerStatus: action,
                // Use marker to indicate current user's profile in rendering
                developerProfileImage: developerImageMarker,
                developerEmail: currentUserEmail || undefined,
                developerActionStatus: action as "acknowledged" | "unclear",
              }
            : item,
        ),
      );

      setIsUpdatingStatus(true);

      try {
        const accessToken = EnhancedTokenManager.getAccessToken();
        if (!accessToken) {
          throw new Error("No access token available");
        }

        const currentUserEmail = EnhancedTokenManager.getCurrentUserEmail();
        if (!currentUserEmail) {
          throw new Error("No user email available");
        }

        const response = await fetch(
          API_URLS.FEEDBACK_DEVELOPER_STATUS_UPDATE(
            parseInt(selectedFeedback.uniqueFeedbackId),
          ),
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              developer_email: currentUserEmail,
              action: action,
            }),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP error! status: ${response.status} - ${errorText}`,
          );
        }

        await response.json();

        setTimeout(() => {
          sessionStorage.removeItem(`status_update_${uniqueId}`);
        }, 2000);

        console.log("✅ Status updated successfully:", { uniqueId, action });
      } catch (error) {
        sessionStorage.removeItem(`status_update_${uniqueId}`);

        setFeedbackStatuses((prev) => ({
          ...prev,
          [uniqueId]: currentStatus || undefined,
        }));

        // ✅ FIXED: Rollback developer info on error
        setLocalFeedbackItems((prev) =>
          prev.map((item) =>
            item.id === selectedFeedback.id
              ? {
                  ...item,
                  developerStatus: currentStatus,
                  developerProfileImage: undefined,
                  developerEmail: undefined,
                  developerActionStatus: undefined,
                }
              : item,
          ),
        );

        console.error("❌ Error updating status:", error);
        alert(
          `Failed to update status: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
        );
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [selectedFeedback, isUpdatingStatus, feedbackStatuses], // profileImage not needed in dependencies
  );

  //  Handle unclear reason toggle
  const handleUnclearReasonToggle = useCallback((reason: string) => {
    setIsBothSelected(false);
    setUnclearReasons((prev) => {
      if (prev.includes(reason)) {
        return prev.filter((r) => r !== reason);
      } else {
        return [...prev, reason];
      }
    });
  }, []);

  // handler for Jira action completion
  const handleJiraActionComplete = useCallback(
    (result: { type: "success" | "error"; title: string; message: string; jiraIssueKey?: string }) => {
      setJiraToastType(result.type);
      setJiraToastTitle(result.title);
      setJiraToastMessage(result.message);
      setShowJiraToast(true);
      if (result.type === "success" && selectedFeedback?.uniqueFeedbackId) {
        const feedbackKey: string = selectedFeedback.uniqueFeedbackId;
        if (result.jiraIssueKey) {
          setJiraButtonStates((prev) => ({
            ...prev,
            [feedbackKey]: {
              isPushed: true,
              jiraIssueKey: result.jiraIssueKey!,
              isLoading: false,
            },
          }));
        } else {
          checkJiraButtonState(feedbackKey);
        }
      }
    },
    [selectedFeedback?.uniqueFeedbackId, checkJiraButtonState],
  );

  //  Handle unclear confirmation
  const handleMarkAsUnclear = useCallback(async () => {
    if (!selectedFeedback?.uniqueFeedbackId || isUpdatingStatus) return;

    // Determine the actual reasons to send to API
    const reasonsToSend = isBothSelected
      ? ["Explanation", "Audio"]
      : unclearReasons;

    if (reasonsToSend.length === 0) {
      alert("Please select at least one reason");
      return;
    }

    const uniqueId = selectedFeedback.uniqueFeedbackId;
    const currentStatus = feedbackStatuses[uniqueId];

    // ✅ NEW: Get current user's email
    const currentUserEmail = EnhancedTokenManager.getCurrentUserEmail();

    // ✅ NEW: Use a special flag to indicate "use current user's profile"
    const developerImageMarker = "__CURRENT_USER__";

    // ✅ STEP 1: Update UI state IMMEDIATELY (before closing popup)
    const updateTimestamp = Date.now();
    sessionStorage.setItem(
      `status_update_${uniqueId}`,
      updateTimestamp.toString(),
    );

    // Optimistically update UI state - this changes the button color instantly
    setFeedbackStatuses((prev) => ({
      ...prev,
      [uniqueId]: "unclear",
    }));

    // ✅ FIXED: Use special marker to indicate current user's profile should be used
    setLocalFeedbackItems((prev) =>
      prev.map((item) =>
        item.id === selectedFeedback.id
          ? {
              ...item,
              developerStatus: "unclear",
              // Use marker to indicate current user's profile in rendering
              developerProfileImage: developerImageMarker,
              developerEmail: currentUserEmail || undefined,
              developerActionStatus: "unclear",
            }
          : item,
      ),
    );

    // ✅ STEP 2: Close popup AFTER updating UI state
    setShowUnclearPopup(false);

    setIsUpdatingStatus(true);

    // ✅ STEP 3: Make API call
    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const currentUserEmail = EnhancedTokenManager.getCurrentUserEmail();
      if (!currentUserEmail) {
        throw new Error("No user email available");
      }

      const requestBody: {
        developer_email: string;
        action: string;
        unclear_reason: string[];
        comment?: string;
      } = {
        developer_email: currentUserEmail,
        action: "unclear",
        unclear_reason: reasonsToSend,
      };

      if (unclearComment.trim()) {
        requestBody.comment = unclearComment.trim();
      }

      const response = await fetch(
        API_URLS.FEEDBACK_DEVELOPER_STATUS_UPDATE(
          parseInt(selectedFeedback.uniqueFeedbackId),
        ),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();
      console.log("✅ Unclear status updated successfully:", result);

      setTimeout(() => {
        sessionStorage.removeItem(`status_update_${uniqueId}`);
      }, 2000);

      setUnclearReasons([]);
      setUnclearComment("");
      setIsBothSelected(false);
    } catch (error) {
      // ✅ STEP 4: Rollback UI changes if API fails
      sessionStorage.removeItem(`status_update_${uniqueId}`);

      setFeedbackStatuses((prev) => ({
        ...prev,
        [uniqueId]: currentStatus || undefined,
      }));

      // ✅ FIXED: Rollback developer info on error
      setLocalFeedbackItems((prev) =>
        prev.map((item) =>
          item.id === selectedFeedback.id
            ? {
                ...item,
                developerStatus: currentStatus,
                developerProfileImage: undefined,
                developerEmail: undefined,
                developerActionStatus: undefined,
              }
            : item,
        ),
      );

      console.error("❌ Error updating unclear status:", error);
      alert(
        `Failed to mark as unclear: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );

      setUnclearReasons([]);
      setUnclearComment("");
      setIsBothSelected(false);
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [
    selectedFeedback,
    isUpdatingStatus,
    feedbackStatuses,
    unclearReasons,
    unclearComment,
    isBothSelected,
  ]);

  const renderFeedbackItem = useCallback(
    (item: FeedbackItem, index: number) => {
      const itemStatus =
        item.uniqueFeedbackId && feedbackStatuses[item.uniqueFeedbackId]
          ? feedbackStatuses[item.uniqueFeedbackId]
          : null;

      const previewText = stripHtmlTags(item.feedback || item.question || "");

      const creatorProfileImageUrl = item.profileImage
        ? `${BASE_URL}${item.profileImage}`
        : AvatarIcon;

      let developerProfileImageUrl = AvatarIcon;

      if (item.developerProfileImage === "__CURRENT_USER__") {
        developerProfileImageUrl = profileImage || AvatarIcon;
        console.log("🖼️ Using current user profile:", profileImage);
      } else if (item.developerProfileImage) {
        if (item.developerProfileImage.startsWith("http")) {
          developerProfileImageUrl = item.developerProfileImage;
        } else {
          developerProfileImageUrl = `${BASE_URL}${item.developerProfileImage}`;
        }
      }

      const displayStatus = item.developerActionStatus || itemStatus;

      return (
        <div
          key={item.id}
          data-feedback-id={item.id}
          onClick={() => handleFeedbackSelect(item.id)}
          className={`p-3 w-full h-28 3xl:h-32 cursor-pointer transition-all relative border-b-[0.5px] border-[#D5D9EA] ${
            selectedFeedbackId === item.id
              ? "bg-[#F3FAFF] border-l-4 border-l-[#49AFFF]"
              : "bg-white hover:bg-gray-50 border-l border-l-transparent"
          }`}
        >
          <div className="flex items-start justify-center gap-3">
            <img
              src={creatorProfileImageUrl}
              alt={item.creatorName || "Creator"}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              onError={(e) => {
                e.currentTarget.src = AvatarIcon;
              }}
            />

            <div className="flex-1 min-w-0 pr-8">
              {previewText ? (
                <p
                  className="text-[12px] 2xl:text-[14px] 3xl:text-[16px] text-[#1E1F26] font-medium mb-2 line-clamp-2"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {previewText}
                </p>
              ) : (
                <p
                  className="text-[12px] 2xl:text-[14px] text-[#1E1F26] mb-2 italic"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  No feedback provided
                </p>
              )}
            </div>

            {/* BOTTOM LEFT: Index, Developer Profile Picture, and Status */}
            <div className="absolute bottom-3 left-3 flex items-center gap-3">
              <span
                className="text-xs 3xl:text-[14px] text-[#1E1F26] font-medium"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {String(index + 1).padStart(2, "0")}
              </span>

              {/* 🆕 REPLACE THIS ENTIRE SECTION WITH TOOLTIP VERSION: */}
              {displayStatus && (
                <div className="flex items-center gap-2">
                  {/* ✅ UPDATED: Developer Profile Picture with Tooltip */}
                  {displayStatus !== "reshare" && (
                    <div
                      className="relative"
                      onMouseEnter={() => {
                        if (!item.uniqueFeedbackId) return;
                        console.log("🔍 Mouse enter - SharedFeedbackView:", {
                          feedbackId: item.uniqueFeedbackId,
                          hasProfileImage: !!developerProfileImageUrl,
                          hasDeveloperName:
                            !!developerNames[item.uniqueFeedbackId],
                          developerName: developerNames[item.uniqueFeedbackId],
                        });
                        setHoveredDeveloperFeedback(
                          item.uniqueFeedbackId || null,
                        );
                      }}
                      onMouseLeave={() => {
                        console.log("🔍 Mouse leave - SharedFeedbackView");
                        setHoveredDeveloperFeedback(null);
                      }}
                    >
                      <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border border-gray-200 cursor-pointer">
                        <img
                          src={developerProfileImageUrl}
                          alt={item.developerEmail || "Developer"}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = AvatarIcon;
                          }}
                        />
                      </div>

                      {/* 🆕 Developer Name Tooltip - RIGHT SIDE */}
                      {(() => {
                        if (!item.uniqueFeedbackId) return null;

                        const shouldShow =
                          hoveredDeveloperFeedback === item.uniqueFeedbackId &&
                          developerNames[item.uniqueFeedbackId];

                        console.log(
                          "🔍 Tooltip render check - SharedFeedbackView:",
                          {
                            shouldShow,
                            hoveredId: hoveredDeveloperFeedback,
                            itemId: item.uniqueFeedbackId,
                            hasName: !!developerNames[item.uniqueFeedbackId],
                            name: developerNames[item.uniqueFeedbackId],
                          },
                        );

                        return shouldShow ? (
                          <div className="absolute left-full top-1/2 ml-2 z-50 animate-tooltip-right">
                            <div className="relative bg-white text-[#1E1F26] text-[14px] font-medium px-3 py-2 rounded-[8px] border border-[#E4E6EE] shadow-lg whitespace-nowrap flex items-center gap-2 -translate-y-1/2">
                              <div className="w-8 h-8 rounded-full overflow-hidden bg-white border border-gray-200">
                                <img
                                  src={developerProfileImageUrl}
                                  alt={developerNames[item.uniqueFeedbackId]}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.src = AvatarIcon;
                                  }}
                                />
                              </div>
                              <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
                                {developerNames[item.uniqueFeedbackId]}
                              </span>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* Status text */}
                  <div
                    className={`text-[12px] 4xl:text-[14px] font-medium ${
                      displayStatus === "acknowledged"
                        ? "text-[#28B446]"
                        : displayStatus === "unclear"
                          ? "text-[#DE860B]"
                          : "text-[#0E73F6]"
                    }`}
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    {displayStatus === "acknowledged"
                      ? "Acknowledged"
                      : displayStatus === "unclear"
                        ? "Unclear"
                        : "Clarified feedback"}
                  </div>
                </div>
              )}
            </div>

            {/* BOTTOM RIGHT: Type Icons */}
            <div className="absolute bottom-3 right-3 flex items-center gap-1">
              {item.type === "screenshot" && item.audioUrl && (
                <Audio className="w-5 h-5" />
              )}
              {item.type === "screenshot" ? (
                <Screenshot className="w-5 h-5" />
              ) : (
                <Video className="w-5 h-5" />
              )}
            </div>
          </div>
        </div>
      );
    },
    [
      selectedFeedbackId,
      handleFeedbackSelect,
      feedbackStatuses,
      profileImage,
      developerNames, // 🆕 ADD THIS
      hoveredDeveloperFeedback, // 🆕 ADD THIS
    ],
  );
  const renderVideoControls = useCallback(
    () => (
      <div className="flex items-center justify-center">
        <div className="flex w-full h-8 xl:h-10 3xl:h-10 4xl:h-12 items-center space-x-2 bg-[#F4F5F6] rounded-[8px] px-4 shadow-sm" style={{ border: '0.5px solid #B7BBCA' }}>
          <button
            onClick={toggleVideoPlayPause}
            className="flex items-center justify-center transition-colors"
          >
            {isVideoPlaying ? (
              <Pause className="w-5 h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7 text-[#1E1F26]" />
            ) : (
              <Play className="w-5 h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7 text-[#1E1F26] ml-0.5" />
            )}
          </button>
          <button
            onClick={skipBackward}
            className="w-8 h-8 3xl:w-10 3xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center text-[#1E1F26]"
          >
            <SkipBackward className="w-5 h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7" />
          </button>
          <button
            onClick={skipForward}
            className="w-8 h-8 3xl:w-10 3xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center text-[#1E1F26]"
          >
            <SkipForward className="w-5 h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7" />
          </button>
          <div className="flex-1 mx-4 relative">
            <div
              className="h-1 bg-gray-300 rounded-full cursor-pointer relative"
              onClick={handleProgressBarClick}
            >
              <div
                className="h-1 bg-[#49AFFF] rounded-full"
                style={{
                  width: `${progressPercentage}%`,
                  transition: isVideoPlaying ? "none" : "width 0.1s ease-out",
                }}
              />
              <div
                className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-[#49AFFF] border-4 border-[#D3E2EB] rounded-full shadow-sm"
                style={{
                  left: `${progressPercentage}%`,
                  marginLeft: "-6px",
                  transition: isVideoPlaying ? "none" : "left 0.1s ease-out",
                }}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2 relative" ref={volumeRef}>
            <button
              onClick={toggleVolumeSlider}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-800"
            >
              {isVideoMuted || videoVolume === 0 ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
            {showVolumeSlider && (
              <div className="absolute bottom-full mb-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[40px] z-10">
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-xs text-gray-600 font-medium">
                    {Math.round(videoVolume * 100)}
                  </div>
                  <div
                    ref={volumeSliderRef}
                    className="relative h-20 w-6 flex items-center justify-center cursor-pointer select-none"
                    onMouseDown={handleVolumeSliderMouseDown}
                  >
                    <div className="w-1 h-full bg-gray-300 rounded-full relative">
                      <div
                        className="absolute bottom-0 w-full bg-[#1E1F26] rounded-full transition-all duration-75 ease-out"
                        style={{ height: `${videoVolume * 100}%` }}
                      />
                    </div>
                    <div
                      className="absolute w-3 h-3 bg-[#1E1F26] rounded-full shadow-sm pointer-events-none transition-all duration-75 ease-out hover:scale-110"
                      style={{
                        bottom: `${videoVolume * 100}%`,
                        left: "50%",
                        transform: "translateX(-50%) translateY(50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    [
      toggleVideoPlayPause,
      isVideoPlaying,
      skipBackward,
      skipForward,
      handleProgressBarClick,
      progressPercentage,
      toggleVolumeSlider,
      isVideoMuted,
      videoVolume,
      showVolumeSlider,
      handleVolumeSliderMouseDown,
    ],
  );

  const renderAudioSection = useCallback(() => {
    if (
      !selectedFeedback ||
      selectedFeedback.type !== "screenshot" ||
      !selectedFeedback.uniqueFeedbackId
    ) {
      return null;
    }

    const uniqueId = selectedFeedback.uniqueFeedbackId;
    const isUploading = isUploadingAudio[uniqueId];
    const error = uploadError[uniqueId];
    const hasAudioUrl = !!selectedFeedback.audioUrl;

    if (isUploading) {
      return (
        <div className="flex items-center justify-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 animate-spin">
              <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <span
              className="text-sm text-blue-700"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              Converting to MP3 and uploading...
            </span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div>
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 text-red-500">⚠️</div>
              <span
                className="text-sm text-red-700 font-medium"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                MP3 Upload Failed
              </span>
            </div>
            <p
              className="text-sm text-red-600 mb-2"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {error}
            </p>
          </div>
          <VoiceNoteRecorder
            key={`voice-recorder-error-${selectedFeedback.id}-${voiceRecorderKey}`}
            initialState="idle"
            onRecordingComplete={handleRecordingComplete}
            disabled={isSubmittedMode}
          />
        </div>
      );
    }

    if (hasAudioUrl && selectedFeedback.audioUrl) {
      return (
        <DeveloperAudioPlayer
          audioUrl={selectedFeedback.audioUrl}
          onError={handleAudioPlayerError}
          onDelete={
            isSubmittedMode
              ? undefined
              : () => handleAudioDelete(selectedFeedback.id, uniqueId)
          }
          className="w-full"
        />
      );
    }

    // Dummy audio player when no audio is available
    return (
      <div className="w-full">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-8 xl:h-10 3xl:h-10 4xl:h-12 bg-[#F4F5F6] rounded-[8px] flex items-center px-3 overflow-hidden relative opacity-50" style={{ border: '0.5px solid #B7BBCA' }}>
            {/* Disabled Play Button */}
            <button
              onClick={handleNoAudioClick}
              className="text-gray-400 flex items-center justify-center mr-4 cursor-pointer hover:text-gray-500 transition-colors"
              aria-label="No audio available"
            >
              <PlayRecording className="w-5 h-5 xl:w-6 xl:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 ml-0.5" />
            </button>

            {/* Empty Progress Bar */}
            <div className="flex-1 relative flex items-center">
              <div className="flex-1 h-1 bg-gray-300 rounded-full relative">
                <div
                  className="h-1 bg-gray-400 rounded-full"
                  style={{ width: "0%" }}
                />
              </div>
            </div>

            {/* "No Audio" Text Overlay */}
            {/* <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-xs 3xl:text-sm text-gray-500 font-medium">
            No audio available
          </span>
        </div> */}
          </div>
        </div>
      </div>
    );
  }, [
    selectedFeedback,
    isUploadingAudio,
    uploadError,
    voiceRecorderKey,
    isSubmittedMode,
    handleRecordingComplete,
    handleAudioPlayerError,
    handleAudioDelete,
  ]);

  const renderFeedbackContent = useCallback(
    (feedback: FeedbackItem | null, isExiting: boolean = false) => {
      if (!feedback) {
        return (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-lg flex items-center justify-center">
                <Camera className="w-6 h-6 text-gray-400" />
              </div>
              <p
                className="text-gray-500"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Select a feedback item to preview
              </p>
            </div>
          </div>
        );
      }

      const uniqueId = feedback.uniqueFeedbackId;
      const currentStatus = uniqueId ? feedbackStatuses[uniqueId] : null;

      // ✅ Check if ANY developer has already taken action on this feedback
      const isActionTakenByAny = uniqueId
        ? actionTakenByAnyDeveloper[uniqueId]
        : false;

      // ✅ UPDATED BUTTON VISIBILITY LOGIC:
      //
      // SHOW BUTTONS when:
      // 1. You have already responded (your status exists and is acknowledged/unclear)
      // 2. It's new feedback that no one has touched yet (no status, no actions)
      // 3. ✅ NEW: It's reshared feedback with NO developer actions yet (status="reshare" && !isActionTakenByAny)
      //
      // HIDE BUTTONS when:
      // 1. Another developer has responded AND you haven't (they took action, you didn't)
      //    - This applies to BOTH normal feedback AND reshared feedback

      const hasUserResponded = currentStatus && currentStatus !== "reshare";
      const isOtherDeveloperResponded = isActionTakenByAny && !hasUserResponded;

      // ✅ KEY CHANGE: Only hide if another developer responded, regardless of reshare status
      const shouldDisableButtons = isOtherDeveloperResponded;

      console.log(`🔍 Button visibility check for feedback ${uniqueId}:`, {
        currentStatus,
        hasUserResponded,
        isActionTakenByAny,
        isOtherDeveloperResponded,
        shouldDisableButtons,
        scenario:
          currentStatus === "reshare" && !isActionTakenByAny
            ? "Reshared + No Actions = SHOW BUTTONS ✅"
            : currentStatus === "reshare" &&
                isActionTakenByAny &&
                !hasUserResponded
              ? "Reshared + Has Actions (other devs) = HIDE BUTTONS 🚫"
              : !currentStatus && isActionTakenByAny && !hasUserResponded
                ? "Normal + Has Actions (other devs) = HIDE BUTTONS 🚫"
                : hasUserResponded
                  ? "User already responded = SHOW BUTTONS ✅"
                  : "New feedback = SHOW BUTTONS ✅",
        reason: shouldDisableButtons
          ? "Another developer has already responded"
          : "You can respond",
      });

      return (
        <div className="h-full flex flex-col">
          {(feedback.feedback || feedback.question) && (
            <div className="px-6 pt-4 bg-white">
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-4">
                  <div className="relative">
                    <div
                      ref={(el) => {
                        if (el && feedback.id) {
                          textContentRefs.current.set(feedback.id, el);
                        }
                      }}
                      className="text-base  font-medium text-[#11142D] prose prose-sm max-w-none relative"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(feedback.feedback || feedback.question || ""),
                      }}
                      style={{
                        fontFamily: DEFAULT_FONT_FAMILY,
                        lineHeight: "1.6",
                        display: "-webkit-box",
                        WebkitLineClamp: textOverflowMap[feedback.id] ? 1 : 999,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        maxHeight: textOverflowMap[feedback.id]
                          ? "2.4em"
                          : "none",
                      }}
                    />
                    {textOverflowMap[feedback.id] && (
                      <div className="inline">
                        <span className="text-[#11142D]">... </span>
                        <button
                          onClick={handleShowMoreClick}
                          className="text-[#4361EE] text-sm font-medium hover:underline inline"
                          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                        >
                          Show more
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 px-6 py-3 overflow-hidden flex flex-col">
            <div className="flex-1 flex items-center justify-center rounded-[14px] min-h-0">
              {activeRightTab === 'context' ? (
                /* Context tab: show environment/technical context data */
                (() => {
                  const feedbackApiId = feedback.apiFeedbackId ? String(feedback.apiFeedbackId) : null;
                  const ctx = feedbackApiId ? environmentCache[feedbackApiId] : undefined;
                  const isLoadingCtx = environmentLoading && !ctx;
                  if (isLoadingCtx) {
                    return (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="flex items-center gap-2 text-[#9CA3AF]">
                          <div className="w-4 h-4 border-2 border-[#9CA3AF] border-t-transparent rounded-full animate-spin" />
                          <p className="text-[14px]" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Loading context...</p>
                        </div>
                      </div>
                    );
                  }
                  if (!ctx) {
                    return (
                      <div className="w-full h-full flex items-center justify-center">
                        <p className="text-[14px] text-[#9CA3AF]" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
                          No context data available for this feedback
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="w-full h-full overflow-auto bg-[#F7FAFD] rounded-lg p-4 custom-scrollbar-overlay">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-[16px] 3xl:text-[18px] font-semibold text-[#192028]" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Environment</span>
                        <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium flex items-center gap-3" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
                          <span>{ctx.timezone}</span>
                          <span>·</span>
                          <span>{ctx.language}</span>
                        </span>
                      </div>

                      {/* Three equal cards */}
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        {/* Platform card */}
                        <div className="bg-white rounded-[10px] border border-[#E4E6EE] overflow-hidden">
                          <div className="px-4 py-2 border-b border-[#E4E6EE]">
                            <span className="text-[14px] 3xl:text-[16px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Platform</span>
                          </div>
                          <div className="px-4 py-3 space-y-3">
                            <div className="flex items-center gap-2.5">
                              <Globe className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-1" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Browser</span>
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.browser}</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                              <Settings2 className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-1" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Browser Version</span>
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.browser_version}</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                              <Monitor className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-1" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>OS</span>
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.os}</span>
                            </div>
                          </div>
                        </div>

                        {/* Display & resolution card */}
                        <div className="bg-white rounded-[10px] border border-[#E4E6EE] overflow-hidden">
                          <div className="px-4 py-2 border-b border-[#E4E6EE]">
                            <span className="text-[14px] 3xl:text-[16px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Display & resolution</span>
                          </div>
                          <div className="px-4 py-3 space-y-3">
                            <div className="flex items-center gap-2.5">
                              <Monitor className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-1" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Device type</span>
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.device_type}</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                              <Maximize2 className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-1" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Resolution</span>
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.resolution}</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                              <Scan className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-1" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Viewport</span>
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.viewport}</span>
                            </div>
                          </div>
                        </div>

                        {/* Network & Localization card */}
                        <div className="bg-white rounded-[10px] border border-[#E4E6EE] overflow-hidden">
                          <div className="px-4 py-2 border-b border-[#E4E6EE]">
                            <span className="text-[14px] 3xl:text-[16px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Network & Localization</span>
                          </div>
                          <div className="px-4 py-3 space-y-3">
                            <div className="flex items-center gap-2.5">
                              <Wifi className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-1" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Internet type</span>
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.network_type}</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                              <Clock className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-1" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Time format</span>
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.time_format}</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                              <Clock className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-1" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Time</span>
                              <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.time}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* App info card */}
                      <div className="bg-white rounded-[10px] border border-[#E4E6EE] overflow-hidden">
                        <div className="px-4 py-2 border-b border-[#E4E6EE]">
                          <span className="text-[14px] 3xl:text-[16px] text-[#5A6572] font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>App info</span>
                        </div>
                        <div className="px-4 py-3 space-y-3">
                          <div className="flex items-center gap-2.5">
                            <Package className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                            <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-shrink-0" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Build version</span>
                            <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium flex-1 text-right" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.build_version}</span>
                          </div>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Link2 className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                            <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-shrink-0" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>URL</span>
                            <a
                              href={ctx.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[13px] font-medium text-[#0E73F6] hover:underline flex items-center justify-end gap-1 flex-1 min-w-0"
                              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                            >
                              <span className="truncate">{ctx.url}</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                          </div>
                          <div className="flex items-center gap-2.5">
                            <Navigation className="w-[17px] h-[17px] text-[#192028] flex-shrink-0" />
                            <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-normal flex-shrink-0" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Route</span>
                            <span className="text-[12px] 3xl:text-[14px] text-[#5A6572] font-medium flex-1 text-right" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>{ctx.route}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : activeRightTab === 'comment' ? (
                /* Comment tab: blank for now */
                null
              ) : (
                /* Issue tab: existing screenshot/video content */
                feedback.type === "screenshot" ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <img
                      src={feedback.annotatedImage}
                      alt="Screenshot preview"
                      className="max-w-full max-h-full object-contain rounded-[14px]"
                    />
                  </div>
                ) : feedback.type === "recording" ? (
                  <div className="w-full h-full flex items-center justify-center rounded-[8px]">
                    <div className="relative w-full h-full flex items-center justify-center rounded-[14px]">
                      <div className="absolute inset-0 w-full flex items-center justify-center rounded-[14px]">
                        {videoUrl && isValidVideoSource(videoUrl) ? (
                          <video
                            ref={videoRef}
                            src={videoUrl}
                            className="w-full h-full object-contain rounded-[14px] bg-white"
                            onPlay={handleVideoPlay}
                            onPause={handleVideoPause}
                            onError={handleVideoError}
                            preload="metadata"
                            playsInline
                            controls={false}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white bg-gray-800">
                            <div className="text-center">
                              <Video className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                              <p className="text-sm">
                                {videoError || "Video source not available"}
                              </p>
                            </div>
                          </div>
                        )}
                        {videoUrl &&
                          isValidVideoSource(videoUrl) &&
                          !isVideoPlaying && (
                            <div
                              className="absolute inset-0 flex items-center justify-center cursor-pointer rounded-[8px] hover:bg-black/30 transition-colors z-10"
                              onClick={toggleVideoPlayPause}
                            >
                              <VideoPlay className="w-32 h-32" />
                            </div>
                          )}
                        {videoUrl &&
                          isValidVideoSource(videoUrl) &&
                          isVideoPlaying && (
                            <div
                              className="absolute inset-0 flex items-center justify-center cursor-pointer rounded-[8px] hover:bg-black/30 transition-colors opacity-0 hover:opacity-100 z-10"
                              onClick={toggleVideoPlayPause}
                            >
                              <VideoPause className="w-32 h-32" />
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <Video className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <p>No video data available</p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
          {!isExiting && feedback.id === selectedFeedbackId && (
            <div>
              {/* Separator — full width */}
              <div className="w-full h-[1px] bg-[#E3E9EF]"></div>
              {/* Glass effect bar — border to border */}
             <div
  className="flex items-center gap-3 px-4 py-3 rounded-b-[10px]
             bg-white/10
             backdrop-blur-md
             border border-white/20
             shadow-lg"
>
                {/* Left: audio/video controls */}
                <div className="flex-1 min-w-0">
                  {feedback.type === "screenshot"
                    ? renderAudioSection()
                    : feedback.type === "recording" &&
                        videoUrl &&
                        isValidVideoSource(videoUrl)
                      ? renderVideoControls()
                      : null}
                </div>

                <div className="w-[2px] bg-[#D5D9EA] self-stretch"></div>

                {/* Right: buttons */}
                {!shouldDisableButtons ? (
                  <div className="flex items-center gap-2 4xl:gap-4 flex-shrink-0">
                    {/* Conditionally render Unclear or Add to Jira button */}
                    {currentStatus === "acknowledged" ? (
                      // Show "Add to Jira" or "Added to Jira" button when acknowledged
                      (() => {
                        const jiraState = uniqueId
                          ? jiraButtonStates[uniqueId]
                          : undefined;
                        const isPushed = jiraState?.isPushed || false;
                        const jiraIssueKey = jiraState?.jiraIssueKey || null;
                        const isLoading = jiraState?.isLoading || false;

                        if (isPushed && jiraIssueKey) {
                          // Show "Added to Jira" with issue key - disabled state
                          return (
                            <button
                              disabled
                              className="w-32 2xl:w-32 3xl:w-36 4xl:w-40 h-8 xl:h-10 3xl:h-10 4xl:h-12 rounded-[6px] text-[14px] 3xl:text-[16px] 4xl:text-[18px] bg-gray-100 text-gray-600 border border-gray-300 cursor-not-allowed flex items-center justify-center gap-2"
                              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                              title={`Already pushed to Jira: ${jiraIssueKey}`}
                            >
                              <span>Added to Jira </span>
                              <img
                                src={jiraIcon}
                                alt="Jira"
                                className="w-4 h-4 3xl:w-5 3xl:h-5 opacity-60"
                              />
                            </button>
                          );
                        }

                        // Show "Add to Jira" button - active state
                        return (
                          <button
                            onClick={() => {
                              console.log(
                                "Add to Jira clicked for feedback:",
                                uniqueId,
                              );
                              setIsJiraPopupOpen(true);
                            }}
                            disabled={isLoading}
                            className="w-32 2xl:w-32 3xl:w-36 4xl:w-40 h-8 xl:h-10 3xl:h-10 4xl:h-12 rounded-[6px] font-medium text-[14px] 3xl:text-[16px] 4xl:text-[18px] bg-white text-[#050505] border border-[#050505] hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                          >
                            <span>
                              {isLoading ? "Checking..." : "Add to Jira"}
                            </span>
                            <img
                              src={jiraIcon}
                              alt="Jira"
                              className="w-4 h-4 3xl:w-5 3xl:h-5"
                            />
                          </button> 
                        );
                      })()
                    ) : (
                      // Show "Unclear" button when not acknowledged
                      <button
                        onClick={() => handleStatusUpdate("unclear")}
                        disabled={
                          currentStatus === "unclear" || isUpdatingStatus
                        }
                        className={`w-32 2xl:w-32 3xl:w-36 4xl:w-40 h-8 xl:h-10 3xl:h-10 4xl:h-12 rounded-[6px] font-medium text-[14px] 3xl:text-[16px] 4xl:text-[18px] transition-colors ${
                          currentStatus === "unclear"
                            ? "bg-yellow-500 text-white border border-yellow-500"
                            : "bg-white text-[#050505] border border-[#050505]"
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        Unclear
                      </button>
                    )}

                    <Button
                      variant="primary"
                      onClick={() => handleStatusUpdate("acknowledged")}
                      disabled={
                        currentStatus === "acknowledged" || isUpdatingStatus
                      }
                      className="w-32 2xl:w-32 3xl:w-36 4xl:w-40 h-8 xl:h-10 3xl:h-10 4xl:h-12 rounded-[6px] text-[14px] 3xl:text-[16px] 4xl:text-[18px]"
                      style={{
                        fontFamily: DEFAULT_FONT_FAMILY,
                        ...(currentStatus === "acknowledged" && { backgroundColor: "#28B446" }),
                      }}
                    >
                      Acknowledge
                    </Button>
                  </div>
                ) : (
                  // ✅ Show message when another developer has already responded
                  <div className="flex items-center gap-2 text-gray-600 bg-[#FFFCF8] border-[0.5px] border-[#FDB857] px-4 py-[10px] rounded-[6px]">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span
                      className="text-[16px] 3xl:text-[18px] font-medium"
                      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      Already marked by another developer
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    },
    [
      selectedFeedbackId,
      videoUrl,
      isVideoPlaying,
      videoError,
      handleVideoPlay,
      handleVideoPause,
      handleVideoError,
      toggleVideoPlayPause,
      renderVideoControls,
      renderAudioSection,
      isSubmittedMode,
      feedbackStatuses,
      actionTakenByAnyDeveloper,
      handleStatusUpdate,
      isUpdatingStatus,
      textOverflowMap,
      handleShowMoreClick,
      jiraButtonStates,
      setIsJiraPopupOpen,
      activeRightTab,
      environmentCache,
      environmentLoading,
    ],
  );

  return (
    <div className="min-h-screen bg-white">
      {/*  Jira Toast Notification */}
      {showJiraToast && (
        <Toast
          type={jiraToastType}
          title={jiraToastTitle}
          message={jiraToastMessage}
          onClose={() => setShowJiraToast(false)}
        />
      )}
      {/* Audio Error Message Notification */}
      {showAudioError && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-orange-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-orange-800">
                  Audio Not Available
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  {audioErrorMessage}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => setShowAudioError(false)}
                  className="bg-orange-50 rounded-md inline-flex text-orange-400 hover:text-orange-500 focus:outline-none"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="w-full">
        <header className="bg-white px-6 pt-2 relative">
          {/* Profile image moved to top-right using absolute positioning */}
          <img
            className="h-10 w-10 rounded-full object-cover absolute right-6 top-4"
            src={profileImage}
            alt="profile picture"
          />

          {isSubmittedMode && (
            <button
              onClick={handleBackToDashboard}
              className="flex items-center gap-1 text-[#1E1F26] hover:text-gray-700 transition-colors mt-3 "
              title="Back to dashboard"
            >
              <ArrowLeft className="w-3 h-3" />
              <span
                className="text-xs 4xl:text-sm font-medium"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Back
              </span>
            </button>
          )}

          <div className="flex justify-between items-center mt-1">
            <div className="flex items-center gap-3 flex-1">
              <h1
                className="text-xl 3xl:text-2xl font-medium text-[#1E1F26]"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {feedbackName}
              </h1>
              {cardId && <DeveloperAvatarsGroup cardId={cardId} />}
            </div>
          </div>
        </header>

        <div className="flex h-[calc(100vh-88px)]">
          <div className="w-[27%] min-w-[240px] max-w-[540px] bg-white">
            <div className="mt-[11px] pl-6 pr-2 mb-1">
              {isSearchOpen ? (
                <div className="flex items-center gap-1" ref={searchInputRef}>
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search feedbacks..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-1 border-[0.5px] font-inter border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#7ABFFF] focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery("");
                    }}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <h2
                    className="text-base font-medium text-[#1E1F26]"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    All feedbacks ({filteredFeedbackItems.length})
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSearchClick}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                    <div className="relative" ref={filterRef}>
                      <button
                        onClick={handleFilterClick}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Filter className="w-4 h-4" />
                      </button>
                      {isFilterOpen && (
                        <div className="absolute right-0 top-full mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                          <div className="p-2 space-y-1">
                            {[
                              { value: "all", label: "All" },
                              { value: "screenshot", label: "Screenshots" },
                              {
                                value: "recording",
                                label: "Screen Recordings",
                              },
                              { value: "acknowledged", label: "Acknowledged" },
                              { value: "unclear", label: "Unclear" },
                              { value: "reshare", label: "Clarified feedback" },
                              { value: "not_read", label: "Not Read" },
                            ].map((filter) => (
                              <button
                                key={filter.value}
                                onClick={() =>
                                  handleFilterSelect(filter.value as any)
                                }
                                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                  selectedFilter === filter.value
                                    ? "bg-[#F8F9FC] text-[#050505]"
                                    : "text-gray-700 hover:bg-[#F8F9FC]"
                                }`}
                                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                              >
                                {filter.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className="mt-2 ml-4 mr-2  h-full rounded-[12px] bg-[#F7F7F7] overflow-y-auto custom-scrollbar-overlay"
              style={{ maxHeight: "calc(100vh - 140px )" }}
              ref={feedbackListRef}
            >
              {filteredFeedbackItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Screenshot className="w-6 h-6" />
                  </div>
                  <p
                    className="text-sm"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    {searchQuery
                      ? "No feedback items match your search"
                      : "No feedback items yet"}
                  </p>
                </div>
              ) : (
                <div
                  className={
                    isListOverflowing
                      ? isMac
                        ? "mx-[10px] my-[10px]"
                        : "ml-[10px] my-[10px]"
                      : "mx-[10px] my-[10px]"
                  }
                >
                  {filteredFeedbackItems.map(renderFeedbackItem)}
                </div>
              )}
            </div>
          </div>
          {/* Right column: tab bar (aligned with "All feedbacks" row) + bordered content */}
          <div className="flex-1 ml-2 mr-4 flex flex-col">
            {/* Tab bar — same row as "All feedbacks" */}
            <div className=" mb-1 flex items-center justify-end">
              <div className="flex items-center gap-0.5 bg-[#EBF0FF] rounded-[10px] p-1">
                <button
                  onClick={() => setActiveRightTab('issue')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[13px] font-medium transition-all ${
                    activeRightTab === 'issue'
                      ? 'bg-white text-[#1E1F26] shadow-sm'
                      : 'text-[#6B7280] hover:text-[#1E1F26]'
                  }`}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  <Info className={`w-3.5 h-3.5 flex-shrink-0 ${activeRightTab === 'issue' ? 'text-[#1E1F26]' : 'text-[#9CA3AF]'}`} />
                  <span>Issue</span>
                </button>
                <button
                  onClick={() => setActiveRightTab('context')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[13px] font-medium transition-all ${
                    activeRightTab === 'context'
                      ? 'bg-white text-[#1E1F26] shadow-sm'
                      : 'text-[#6B7280] hover:text-[#1E1F26]'
                  }`}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  <Code2 className={`w-3.5 h-3.5 flex-shrink-0 ${activeRightTab === 'context' ? 'text-[#00B8D9]' : 'text-[#9CA3AF]'}`} />
                  <span>Context</span>
                </button>
                {/* <button
                  onClick={() => setActiveRightTab('comment')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[13px] font-medium transition-all ${
                    activeRightTab === 'comment'
                      ? 'bg-white text-[#1E1F26] shadow-sm'
                      : 'text-[#6B7280] hover:text-[#1E1F26]'
                  }`}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  <MessageSquare className={`w-3.5 h-3.5 flex-shrink-0 ${activeRightTab === 'comment' ? 'text-[#1E1F26]' : 'text-[#9CA3AF]'}`} />
                  <span>Comment</span>
                </button> */}
              </div>
            </div>

            {/* Bordered content panel */}
            <div
              ref={contentContainerRef}
              className="flex-1 mt-2 bg-white border border-[#C5D0F5] rounded-[10px] shadow-sm overflow-hidden relative"
            >
              {isAnimating && previousFeedback && (
                <div
                  className={`absolute inset-0 bg-white transition-transform duration-300 ease-out ${
                    scrollDirection === "down"
                      ? "-translate-y-full"
                      : "translate-y-full"
                  }`}
                  style={{ zIndex: 1 }}
                >
                  {renderFeedbackContent(previousFeedback, true)}
                </div>
              )}
              <div
                className={`absolute inset-0 bg-white transition-transform duration-300 ease-out ${
                  isAnimating
                    ? scrollDirection === "down"
                      ? "translate-y-0"
                      : "translate-y-0"
                    : "translate-y-0"
                }`}
                style={{
                  zIndex: 2,
                  transform: isAnimating
                    ? scrollDirection === "down"
                      ? "translateY(0)"
                      : "translateY(0)"
                    : "translateY(0)",
                  ...(isAnimating && {
                    animation:
                      scrollDirection === "down"
                        ? "slideFromBottom 0.3s ease-out"
                        : "slideFromTop 0.3s ease-out",
                  }),
                }}
              >
                {renderFeedbackContent(selectedFeedback || null)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/*  Unclear Feedback Popup */}
      {showUnclearPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
            <div className="px-4 py-4">
              {/* Header */}
              <div className="flex items-center justify-between mt-2 mb-2">
                <h3
                  className="text-[18px] 3xl:text-[20px] font-medium text-[#070707] text-left"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Unclear feedback?
                </h3>
                <button
                  onClick={() => {
                    setShowUnclearPopup(false);
                    setUnclearReasons([]);
                    setUnclearComment("");
                    setIsBothSelected(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-5 h-5 3xl:w-6 3xl:h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-6" />

              {/* Description */}
              <p
                className="text-[#070707] text-sm 3xl:text-[16px] mb-4"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Select the unclear feedback
              </p>

              {/* Unclear Reason Buttons */}
              <div className="flex gap-3 mb-4">
                <button
                  onClick={() => handleUnclearReasonToggle("Explanation")}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-lg 4xl:text-xl transition-all ${
                    !isBothSelected && unclearReasons.includes("Explanation")
                      ? "bg-black text-white"
                      : "bg-white text-black border-[0.5px] border-[#1E1F26] hover:bg-gray-50"
                  }`}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Explanation
                </button>
                <button
                  onClick={() => handleUnclearReasonToggle("Audio")}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-lg 4xl:text-xl transition-all ${
                    !isBothSelected && unclearReasons.includes("Audio")
                      ? "bg-black text-white"
                      : "bg-white text-black border-[0.5px] border-[#1E1F26] hover:bg-gray-50"
                  }`}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Audio
                </button>
                {/* Separator */}
                <div className="border-r border-[#CBCED9] my-1" />
                <button
                  onClick={() => {
                    setIsBothSelected(true);
                    setUnclearReasons([]);
                  }}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-lg 4xl:text-xl transition-all ${
                    isBothSelected
                      ? "bg-black text-white"
                      : "bg-white text-black border-[0.5px] border-[#1E1F26] hover:bg-gray-50"
                  }`}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Both
                </button>
              </div>

              {/* Comment Input */}
              <textarea
                value={unclearComment}
                onChange={(e) => setUnclearComment(e.target.value)}
                placeholder="Add comments (Optional)"
                className="w-full px-3 py-2 bg-[#F8F9FC] border-[0.5px] border-[#E6E8EE] rounded-lg focus:outline-none focus:ring-1 focus:ring-black resize-none text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-lg 4xl:text-xl mb-6"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                rows={3}
              />

              {/* Separator */}
              <div className="border-t border-gray-200 mb-4" />

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="cancel"
                  onClick={() => {
                    setShowUnclearPopup(false);
                    setUnclearReasons([]);
                    setUnclearComment("");
                    setIsBothSelected(false);
                  }}
                   size="lg"
                  className="w-full sm:w-auto text-sm"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleMarkAsUnclear}
                  disabled={
                    (unclearReasons.length === 0 && !isBothSelected) ||
                    isUpdatingStatus
                  }
                  size="lg"
                  className="w-full sm:w-auto text-sm"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {isUpdatingStatus ? "Updating..." : "Mark as unclear"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ShareFeedbackPopup
        isOpen={isSharePopupOpen}
        onClose={() => setIsSharePopupOpen(false)}
        title={`Share "${feedbackName || "feedback"}" with developers`}
        totalFeedback={totalFeedbacks}
        onShare={handleShareEmails}
        onCancel={() => setIsSharePopupOpen(false)}
      />

      <JiraIntegrationPopup
        isOpen={isJiraPopupOpen}
        onClose={() => setIsJiraPopupOpen(false)}
        feedbackId={selectedFeedback?.uniqueFeedbackId}
        onJiraActionComplete={handleJiraActionComplete}
      />

      {/* Edit Feedback Popup (Read-only mode) */}
      {showEditPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-xl xl:max-w-xl 2xl:max-w-2xl 4xl:max-w-3xl w-full mx-4">
            <div className="px-4 py-4">
              {/* Text Editor - Read-only */}
              <div className="mb-3" style={{ height: "300px" }}>
                <div
                  className="w-full h-full px-4 py-3 bg-[#F8F9FC] border-[0.5px] border-[#E6E8EE] rounded-lg overflow-y-auto prose prose-sm max-w-none custom-scrollbar-overlay"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(editedFeedbackText),
                  }}
                  style={{
                    lineHeight: "1.6",
                  }}
                />
              </div>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-3" />

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelEdit}
                  className="w-full sm:w-auto text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-xl 4xl:text-2xl px-4 sm:px-6 xl:px-8 2xl:px-10 4xl:px-12 py-1.5 sm:py-1.5 xl:py-1.5 2xl:py-2 rounded-lg border border-gray-300 text-black font-medium hover:bg-gray-50 transition-colors"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        @keyframes slideFromBottom {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes slideFromTop {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
        .transition-transform {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .duration-300 {
          transition-duration: 300ms;
        }
           /* HTML Content Rendering Styles */
  .prose strong {
    font-weight: 700;
  }

  .prose em {
    font-style: italic;
  }

  .prose u {
    text-decoration: underline;
  }

  .prose ul {
    list-style-type: disc;
    padding-left: 1.5rem;
    margin: 0.5rem 0;
  }

  .prose ol {
    list-style-type: decimal;
    padding-left: 1.5rem;
    margin: 0.5rem 0;
  }

  .prose li {
    margin: 0.25rem 0;
  }

  .prose a {
    color: #2563eb;
    text-decoration: underline;
    cursor: pointer;
  }

  .prose a:hover {
    color: #1e40af;
  }

  .prose p {
    margin: 0.5rem 0;
  }

  .prose p:first-child {
    margin-top: 0;
  }

  .prose p:last-child {
    margin-bottom: 0;
  }
   /* 🆕 NEW: Tooltip animation for developer names */
@keyframes tooltip-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.animate-tooltip {
  animation: tooltip-in 0.15s ease-out;
}

 
      `}</style>
    </div>
  );
};

export default SharedFeedbackView;
